import './FlyoutPlan.css';

import React, {useContext, useEffect, useState} from "react";
import {useMediaQuery} from 'react-responsive';

import {FlyoutPlanContext} from "./flyout-plan-context.js";
import FlyoutLeg from "./FlyoutLeg.jsx";
import FlyoutRefuel from "./FlyoutRefuel.jsx";
import FlyoutMap from "./FlyoutMap.jsx";
import FlyoutPlaneState from "./FlyoutPlaneState.jsx";

import { getAirportDetails } from "../assets/virtual-flyout/airport-service.js";


export default function FlyoutPlan() {

    const {plan, activeDay, ctxPlanSwitchDay, ctxPlanAddLeg, ctxPlanModifyLeg, ctxPlanDeleteLeg} = useContext(FlyoutPlanContext);
    const currentDay = plan.days.find(day => day.name === activeDay);

    const isMobile = useMediaQuery({query: '(max-width: 30em)'});
    const [displayMode, setDisplayMode] = useState("PLAN");

    function handleAddLeg(indexBefore, type = "flight") {
        // derive "from" of the new leg from the "to" of the previous leg or the start point
        let prevWaypoint = currentDay.startPoint;
        if (indexBefore > 0) {
            prevWaypoint = currentDay.legs[indexBefore - 1].type === "flight" ?
                currentDay.legs[indexBefore - 1].segments.slice(-1)[0].to :
                currentDay.legs[indexBefore - 1].location;
        }
        const newLeg = (type === "refuel") ?
            {type: "refuel", location: prevWaypoint, fuelQty: 0, fuelUnit: "US gallons", state: "NEW"} :
            {
                type: "flight", departureTime: "",
                segments: [{from: prevWaypoint, to: null, altitude: 3}], 
                state: "NEW"
            };
        ctxPlanAddLeg(newLeg, indexBefore);
    }

    function handleDeleteLeg(index) {
        ctxPlanDeleteLeg(index);
    }

    function handleModifyLeg(index, leg) {
        ctxPlanModifyLeg(index, leg);
    }

    function handleSwitchDay(day) {
        ctxPlanSwitchDay(day);
    }

    function canAddLeg(index, type) {
        // allow anything if the day is empty
        if (currentDay.legs.length === 0) {
            return true;
        }

        // allow only one refuel between two flights
        const prevLeg = (index > 0) ? currentDay.legs[index - 1] : null;
        const nextLeg = (index < currentDay.legs.length) ? currentDay.legs[index] : null;
        if (type === "refuel") {
            if (prevLeg && prevLeg.type === "refuel") {
                return false;
            }
            if (nextLeg && nextLeg.type === "refuel") {
                return false;
            }
        }

        // if previous location and next location are the same, don't allow flight
        const prevLocation = prevLeg 
            ? (prevLeg.type === "flight" 
                ? (prevLeg.segments.slice(-1)[0].to?.code || null) 
                : (prevLeg.location.code)) 
            : (currentDay.startPoint.code);
        const nextLocation = nextLeg 
            ? (nextLeg.type === "flight" 
                ? (nextLeg.segments[0].from?.code || null) 
                : (nextLeg.location.code)) 
            : (currentDay.endPoint.code);
        if (type === "flight") {
            return prevLocation !== nextLocation;
        }

        return true;
    }

    const startAirport = currentDay?.startPoint ? getAirportDetails(currentDay.startPoint.code) : null;
    const endAirport = currentDay?.endPoint ? getAirportDetails(currentDay.endPoint.code) : null;
    function formatAirport(airport) {
        return `${airport.airport_name} (${airport.airport_code})`;
    }

    return (
        <div className="flyout-plan">
            <div className="flyout-plan-header">
                <div className="flyout-plan-day-tabs">
                    <button key="smry" tabIndex="-1" className={`flyout-plan-day-tab ${!activeDay ? "active" : ""}`}
                            onClick={() => handleSwitchDay(null)}>Main
                    </button>
                    {plan.days.map((day, index) => (
                        <button key={index} tabIndex="-1"
                                className={`flyout-plan-day-tab 
                                ${activeDay === day.name ? "active" : ""}`}
                                onClick={() => handleSwitchDay(day.name)}>
                            {day.name}
                        </button>
                    ))}
                </div>
                {isMobile && displayMode === "PLAN" &&
                    <button className="flyout-plan-leg-header-button" tabIndex="-1" title="Switch to Map"
                            onClick={() => setDisplayMode("MAP")}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                             stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z"/>
                        </svg>
                    </button>}
                {isMobile && displayMode === "MAP" &&
                    <button className="flyout-plan-leg-header-button" tabIndex="-1" title="Switch to Plan"
                            onClick={() => setDisplayMode("PLAN")}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5}
                             stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round"
                                  d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z"/>
                        </svg>
                    </button>}
            </div>

            {!currentDay && displayMode === "PLAN" && <FlyoutPlaneState planeId={plan.plane.reg}/>}

            {(isMobile && displayMode === "MAP") ? <FlyoutMap/> : null}

            {displayMode === "PLAN" && currentDay && <div className="flyout-plan-main">
                {currentDay.startPoint && <div className="flyout-plan-point">
                    <h3>Start:</h3>
                    <p>{formatAirport(startAirport)}</p>
                </div>}
                <div className="flyout-plan-buttons">
                    {canAddLeg(0, "refuel") && <button className="flyout-plan-button" tabIndex="-1"
                                                       onClick={() => handleAddLeg(0, "refuel")}>Add Refuel</button>}
                    {canAddLeg(0, "flight") && <button className="flyout-plan-button" tabIndex="-1"
                                                       onClick={() => handleAddLeg(0, "flight")}>Add Leg</button>}
                </div>
                {currentDay.legs.map((leg, index) => (<React.Fragment key={index}>
                    {leg.type === "flight" && <FlyoutLeg leg={leg} index={index}
                                                         onChange={handleModifyLeg} onDelete={handleDeleteLeg}/>}
                    {leg.type === "refuel" && <FlyoutRefuel refuel={leg} index={index}
                                                            onChange={handleModifyLeg} onDelete={handleDeleteLeg}/>}

                    <div key={index + "_btn"} className="flyout-plan-buttons">
                        {canAddLeg(index + 1, "refuel") &&
                            <button key={index + "_addRefuel"} className="flyout-plan-button" tabIndex="-1"
                                    onClick={() => handleAddLeg(index + 1, "refuel")}>Add
                                Refuel</button>}
                        {canAddLeg(index + 1, "flight") &&
                            <button key={index + "_addLeg"} className="flyout-plan-button" tabIndex="-1"
                                    onClick={() => handleAddLeg(index + 1, "flight")}>Add
                                Leg</button>}
                    </div>
                </React.Fragment>))}
                {currentDay.endPoint && <div className="flyout-plan-point">
                    <h3>End:</h3>
                    <p>{formatAirport(endAirport)}</p>
                </div>}
            </div>}
        </div>
    )
}